import React, { useEffect, useMemo, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import currency from 'currency.js'
import styles from './Yearbooks.module.scss'
import { OrderPackage } from '../../../models'
import Modal from '../../common/Modal'
import axios from 'axios';
import { capitalize } from '@material-ui/core';

const CaretIcon = () => (
  <svg style={{ minWidth: '14px', maxWidth: '14px', marginRight: '6px' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M9.39 265.4l127.1-128C143.6 131.1 151.8 128 160 128s16.38 3.125 22.63 9.375l127.1 128c9.156 9.156 11.9 22.91 6.943 34.88S300.9 320 287.1 320H32.01c-12.94 0-24.62-7.781-29.58-19.75S.2333 274.5 9.39 265.4z"/></svg>
)

const formattedDate = (dateString) => format(new Date(dateString), 'MMM d yy')

const useCounts = (onlineOrders) => {
  const [hardCovers, setHardCovers] = useState(0)
  const [hardCoverUpgrades, setHardCoverUpgrades] = useState(0)
  const [softCovers, setSoftCovers] = useState(0)
  const [softCoverPerfectBound, setSoftCoverPerfectBound] = useState(0)
  const [personalizations, setPersonalizations] = useState(0)
  const [ordersWithoutPrice, setOrdersWithoutPrice] = useState(0)
  const [improperPackages, setImproperPackages] = useState(0)

  useEffect(() => {
    const hardcovers = onlineOrders.filter((orderPackage) => orderPackage.package.id === 648).length
    const hardcoverUpgrades = onlineOrders.filter((orderPackage) => orderPackage.package.id === 497).length
    const softcovers = onlineOrders.filter((orderPackage) => orderPackage.package.id === 646).length
    const softCoverPerfectBoundCount = onlineOrders.filter((orderPackage) => orderPackage.package.id === 647).length
    const personalizationCount = onlineOrders.filter((orderPackage) => orderPackage.package.id === 496).length
    const ordersWithoutPriceCount = onlineOrders.filter((orderPackage) => parseInt(orderPackage.price, 10) === 0 && !orderPackage.order.school_price).length
    const improperPackagesCount = onlineOrders.filter((orderPackage) => orderPackage.improper_package).length

    setHardCovers(hardcovers)
    setHardCoverUpgrades(hardcoverUpgrades)
    setSoftCovers(softcovers)
    setSoftCoverPerfectBound(softCoverPerfectBoundCount)
    setPersonalizations(personalizationCount)
    setOrdersWithoutPrice(ordersWithoutPriceCount)
    setImproperPackages(improperPackagesCount)
  }, [JSON.stringify(onlineOrders)])

  return { hardCovers, hardCoverUpgrades, softCovers, softCoverPerfectBound, ordersWithoutPrice, personalizations, improperPackages }
}

const useSorter = (initialData) => {
  const [originalData, setOriginalData] = useState([])
  const [sortedData, setSortedData] = useState([...initialData])
  const [key, setKey] = useState(null)
  const [dir, setDir] = useState(null)

  const changeSort = (nextKey) => {
    if (nextKey === key) {
      if (dir === 'ASC') {
        setDir('DESC')
      } else if (dir === 'DESC') {
        setKey(null)
        setDir(null)
      }
    } else { // changing keys, so reset the dir
      setKey(nextKey)
      setDir('ASC')
    }
  }

  const removeOrderPackage = (id) => {
    setSortedData([...sortedData.filter((orderPackage) => orderPackage.id !== id)])
  }

  useEffect(() => {
    setOriginalData([...initialData])
  }, [])

  useEffect(() => {
    if (originalData.length === 0) return
    if (!(key && dir)) {
      setSortedData([...originalData])
      return
    }

    const newData = sortedData.sort((a, b) => {
      if (key !== 'price' && key !== 'improper_package' && !a.student[key]) return -1
      if (key !== 'price' && key !== 'improper_package' && !b.student[key]) return 1
      if (dir === 'ASC') {
        if (key === 'price') return parseInt(a.price, 10) - parseInt(b.price, 10)
        else if (key === 'improper_package') return b.improper_package
        return a.student[key].localeCompare(b.student[key])
      }

      if (key === 'price') return parseInt(b.price, 10) - parseInt(a.price, 10)
      else if (key === 'improper_package') return a.improper_package
      return b.student[key].localeCompare(a.student[key])
    })

    setSortedData([...newData])
  }, [key, dir])

 return { key, dir, changeSort, sortedData, removeOrderPackage }
}

function Counts({ onlineOrders, isAdmin }) {
  const {
    hardCovers,
    hardCoverUpgrades,
    softCovers,
    softCoverPerfectBound,
    ordersWithoutPrice,
    personalizations,
    improperPackages,
  } = useCounts(onlineOrders)

  const yearbookCounts = [
    { label: '$0 Orders', value: ordersWithoutPrice },
    { label: 'Hard covers', value: hardCovers },
    { label: 'Hard cover upgrades', value: hardCoverUpgrades },
    { label: 'Soft covers', value: softCovers },
    { label: 'Soft covers (Perfect bound)', value: softCoverPerfectBound },
    { label: 'Personalizations', value: personalizations },
  ]

  if (isAdmin) {
    yearbookCounts.unshift({ label: 'Improper Packages', value: improperPackages })
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
      {yearbookCounts.map(({ label, value }) => {
        if (value === 0) return null
        if (label === '$0 Orders' && !isAdmin) return null

        return (
          <span key={label}>
            <strong>{label}</strong>
            :
            <span style={{ marginLeft: '4px' }}>{value}</span>
          </span>
        )
      })}
    </div>
  )
}

function TotalSales({ onlineOrders }) {
  const totalSales = useMemo(() => (
    onlineOrders.reduce((acc, cur) => {
      return acc + parseInt(cur.price, 10)
    }, 0)
  ), [JSON.stringify(onlineOrders)])

  return (
    <div style={{ margin: '6px 0 24px', display: 'flex', justifyContent: 'flex-start' }}>
      <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Total Sales on Website:</span>
      {currency(totalSales).format()}
    </div>
  );
}

const studentKey = (student) => `${(student.first_name || '').toLowerCase().replace(/\s/g, '')}-${(student.last_name || '').toLowerCase().replace(/\s/g, '')}`

function StudentsTotalRow({ orderPackage, count, index, totalForStudent }) {
  return (
    <tr>
      <td>{index === 0 ? `${orderPackage.student.first_name} ${orderPackage.student.last_name}` : ''}</td>
      <td>{`${count} x ${orderPackage.package.name}`}</td>
      <td>{index === 0 && totalForStudent}</td>
    </tr>
  )
}

export function TotalsPerStudentModal({ onlineOrders, school }) {
  const [isOpen, setIsOpen] = useState(false)
  const [formattedData, setFormattedData] = useState([])

  const generateCSV = () => {
    axios({
      url: '/order_packages/formatted_csv',
      method: 'POST',
      responseType: 'blob',
      data: formattedData,
    })
    .then((res) => {
      const formattedSchoolName = (school.name || '').toLowerCase().trim().replace(/\s/g, '_')
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `yearbooks_per_student_${formattedSchoolName}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
  }

  const data = useMemo(() => {
    return onlineOrders.reduce((acc, orderPackage) => {
      // const key = orderPackage.student.id
      const { student } = orderPackage
      const key = studentKey(student)

      if (acc[key]) {
        if (acc[key][orderPackage.package.id]) {
          const datum = acc[key][orderPackage.package.id]

          acc[key][orderPackage.package.id] = { ...datum, count: datum.count + 1 }
        } else {
          acc[key][orderPackage.package.id] = { orderPackage, count: 1 }
        }
      }
      else {
        acc[key] = { [orderPackage.package.id]: { orderPackage, count: 1 } }
      }

      return acc
    }, {})
  }, [JSON.stringify(onlineOrders)])

  useEffect(() => {
    const formatted = Object
      .entries(data)
      .sort((a, b) => {
        return a[0].localeCompare(b[0])
      })
      .map((orderPackagesByStudentId) => {
        return Object.entries(orderPackagesByStudentId[1]).map((orderPackagesByPackageId, i) => {
          const yearbookData = orderPackagesByPackageId[1]

          const totalYearbooksForStudent = Object
            .values(orderPackagesByStudentId[1])
            .filter((datum) => {
              const {orderPackage: {package: {id: packageId}}} = datum

              return packageId !== 496 && packageId !== 497
            })
            .map((datum) => datum.count)
            .reduce((acc, count) => acc + count, 0)

          const { orderPackage: op, count } = yearbookData

          return [i === 0 ? `${op.student.first_name} ${op.student.last_name}` : '', `${count} x ${op.package.name}`, i === 0 ? totalYearbooksForStudent : '']
        })
      })

    setFormattedData(formatted)
  }, [JSON.stringify(data)])

  return (
    <>
      <div className={styles.modalBtnContainer}>
        <button className="btn" onClick={() => setIsOpen(true)}>See orders per student</button>
      </div>
      <Modal modalIsOpen={isOpen} closeModal={() => setIsOpen(false)} className={styles.modalClass}>
        <div className={styles.modalBtnContainer}>
          <button className="btn" onClick={generateCSV}>Generate CSV</button>
        </div>
        <table className="table table-striped">
          <thead>
            <tr>
              <td>Student</td>
              <td>Yearbooks</td>
              <td>Total Yearbooks</td>
            </tr>
          </thead>
          <tbody>
            {Object
              .entries(data)
              .sort((a, b) => {
                return a[0].localeCompare(b[0])
              })
              .map((orderPackagesByStudentId) => {
                return Object.entries(orderPackagesByStudentId[1]).map((orderPackagesByPackageId, i) => {
                  const yearbookData = orderPackagesByPackageId[1]

                  const totalYearbooksForStudent = Object
                    .values(orderPackagesByStudentId[1])
                    .filter((datum) => {
                      const { orderPackage: { package: { id: packageId } } } = datum

                      return packageId !== 496 && packageId !== 497
                    } )
                    .map((datum) => datum.count)
                    .reduce((acc, count) => acc + count, 0)

                  return (
                    <StudentsTotalRow
                      key={yearbookData.orderPackage.id}
                      orderPackage={yearbookData.orderPackage}
                      count={yearbookData.count}
                      index={i}
                      totalForStudent={totalYearbooksForStudent}
                    />
                  )
                })
              })}
          </tbody>
        </table>
      </Modal>
    </>
  )
}

function OrderPackageCells({ school, orderPackage, isAdmin, removeOrderPackage, prevOrderPackage }) {
  const useErrorStyle = isAdmin && parseInt(orderPackage.price, 10) === 0 && !orderPackage.order.school_price
  const displayOrderId = !prevOrderPackage || (prevOrderPackage && prevOrderPackage.order.id !== orderPackage.order.id)

  const deleteOrderPackage = async () => {
    const { data: op } = await OrderPackage.find(orderPackage.id)

    op
      .destroy()
      .then(() => {
        removeOrderPackage(orderPackage.id)
        toast('Successfully deleted.', { type: 'success' })
      })
      .catch(() => {
        toast('Error deleting record. Please try again later.', { type: 'error' })
      })
  }

  return (
    <tr className={useErrorStyle ? styles.error : ''}>
      <td style={{ whiteSpace: 'nowrap' }}>
        {displayOrderId && orderPackage.order.id}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{formattedDate(orderPackage.created_at)}</td>
      <td>{orderPackage.package.name}</td>
      <td>{orderPackage.student.first_name}</td>
      <td>{orderPackage.student.last_name}</td>
      <td>{orderPackage.student.teacher || ''}</td>
      <td>{currency(orderPackage.school_price || orderPackage.price).format()}</td>
      {isAdmin && (
        <td>{orderPackage.improper_package ? 'Yes' : ''}</td>
      )}
      <td>{orderPackage.order.school_price ? 'Manual' : 'Online'}</td>
      <td>{orderPackage.customized ? 'True' : 'False'}</td>
      <td>{isAdmin && orderPackage.yearbook_cover_printed ? 'True' : 'False'}</td>
      <td>{isAdmin && orderPackage.ship_date ? orderPackage.ship_date : 'Not shipped'}</td>
      <td>{orderPackage.order.notes}</td>
      <td>{isAdmin && orderPackage.order.admin_notes}</td>
      <td><a href={`/order_packages/${orderPackage.id}/edit`}>Edit</a></td>
      <td>
        {isAdmin && (
          <button type="button"
                  onClick={() => { window.confirm('Are you sure to delete this record?') && deleteOrderPackage()}}
                  className="btn btn-danger">
            Delete
          </button>
        )}
      </td>

      <td>{isAdmin && (
        <>
          <a href={`/admin/order_packages/${orderPackage.id}/clear?status=print&school_id=${school.id}`}>Clear Print Status</a><br/>
          <a href={`/admin/order_packages/${orderPackage.id}/clear?status=ship&school_id=${school.id}`}>Clear Ship Status</a>
        </>
        )}</td>
    </tr>
  )
}

function orderPackageCellData(orderPackage, isAdmin, prevOrderPackage) {
  const displayOrderId = !prevOrderPackage || (prevOrderPackage && prevOrderPackage.order.id !== orderPackage.order.id)

  return ([
    displayOrderId ? orderPackage.order.id : '',
    formattedDate(orderPackage.created_at),
    orderPackage.package.name,
    orderPackage.student.first_name,
    orderPackage.student.last_name,
    orderPackage.student.teacher || '',
    currency(orderPackage.school_price || orderPackage.price).format(),
    orderPackage.order.school_price ? 'Manual' : 'Online',
    orderPackage.customized ? 'True' : 'False',
    isAdmin && orderPackage.yearbook_cover_printed ? 'True' : 'False',
    isAdmin && orderPackage.ship_date ? orderPackage.ship_date : 'Not shipped',
    orderPackage.order.notes,
    isAdmin ? orderPackage.order.admin_notes : '',
    isAdmin ? orderPackage.id : '',
  ])
}

export function generateYearbookCSV(data, isAdmin, school) {
  const formattedData = data.map((orderPackage, i) => orderPackageCellData(orderPackage, isAdmin, data[i - 1]))

  formattedData.unshift([
    'Order ID', 'Date', 'Package', 'Student Fname', 'Student Lname', 'Student Teacher', 'Price', 'Order Type', 'Customized', isAdmin ? 'Cover Printed' : '', isAdmin ? 'Ship Date' : '', 'Notes', isAdmin ? 'Admin Notes' : '', isAdmin ? 'Item ID' : '', isAdmin ? 'Image URL' : '', isAdmin ? 'Error' : ''
  ])

  axios({
    url: '/order_packages/csv_from_data',
    method: 'POST',
    responseType: 'blob',
    data: formattedData,
  })
    .then((res) => {
      const formattedSchoolName = (school.name || '').toLowerCase().trim().replace(/\s/g, '_')
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `yearbooks_${formattedSchoolName}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
}

function YearbookTotals({ onlineOrders = [] }) {
  const totals = useMemo(() => (
    onlineOrders.reduce((acc, cur) => ({
      ...acc,
      [cur.package.name]: acc[cur.package.name] !== undefined ? acc[cur.package.name] + 1 : 1,
    }), {})
  ), [JSON.stringify(onlineOrders)]);

  return (
    <>
      {Object.keys(totals).map((key) => (
        <div key={key} style={{ margin: '6px 0', display: 'flex', justifyContent: 'flex-start' }}>
          <span style={{ fontWeight: 'bold', marginRight: '4px' }}>
            Total
            {' '}
            {key}
            {' '}
            Sold on Website:
          </span>
          {totals[key]}
        </div>
      ))}
    </>
  );
}

export default function Yearbooks({ onlineOrders, currentUser, school, yearbookAdminJob, yearbookContractDetails = [] }) {
  const { key, dir, changeSort, sortedData, removeOrderPackage } = useSorter(onlineOrders)

  const sortStyle = dir === 'DESC' ? { transform: 'rotate(180deg) translateY(7px)' } : {}

  return (
    <div>
      <ToastContainer theme="light" />
      {/* Detail block: left-aligned and indented under the "{School} Yearbook Orders"
          header in the parent ERB. Action buttons (Generate CSV / See orders per
          student) live in the YearbookHeaderActions component mounted next to the
          school dropdown. */}
      <div style={{ paddingLeft: '24px' }}>
        <Counts onlineOrders={sortedData} isAdmin={currentUser.admin} />
        <YearbookTotals onlineOrders={sortedData} yearbookContractDetails={yearbookContractDetails} />
        {currentUser.admin && yearbookContractDetails.map((contractDetail) => (
          <div key={contractDetail.id || contractDetail.binding_type} style={{ margin: '6px 0 24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>
              {capitalize(contractDetail.binding_type.split('_').join(' '))}s Contract Details
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', columnGap: '24px', rowGap: '4px', justifyItems: 'start' }}>
              <div>
                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Number of extras:</span>
                {contractDetail.num_of_extras}
              </div>
              <div>
                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Price per Book:</span>
                {currency(contractDetail.price_per_book).format()}
              </div>
              <div>
                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Presale Price:</span>
                {currency(contractDetail.presale_price).format()}
              </div>
              <div>
                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Final Sale Price:</span>
                {currency(contractDetail.final_sale_price).format()}
              </div>
              <div>
                <span style={{ fontWeight: 'bold', marginRight: '4px' }}>Shipping:</span>
                {currency(contractDetail.shipping).format()}
              </div>
            </div>
          </div>
        ))}
        {currentUser.admin && <TotalSales onlineOrders={sortedData} />}
      </div>
      <div className={styles.directions}>Orders without an ID have the same ID as the one above it</div>
      <div className={styles.directions}>Click Student First Name, Student Last Name, Teacher or Price headers to sort the table</div>
      <table className="table table-striped">
        <thead>
          <tr>
            <td>
              <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                Order ID
                {!(key || dir) && (
                  <div style={{ width: '14px' }}>
                    <CaretIcon />
                  </div>
                )}
              </span>
            </td>
            <td>Date</td>
            <td>Package</td>
            <td onClick={() => changeSort('first_name')} style={{ cursor: 'pointer' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                Student First Name
                {key === 'first_name' && (
                  <div style={{ ...sortStyle, width: '14px' }}>
                    <CaretIcon />
                  </div>
                )}
              </span>
            </td>
            <td onClick={() => changeSort('last_name')} style={{ cursor: 'pointer' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                Student Last Name
                {key === 'last_name' && (
                  <div style={{ ...sortStyle, width: '14px' }}>
                    <CaretIcon />
                  </div>
                )}
              </span>
            </td>
            <td onClick={() => changeSort('teacher')} style={{ cursor: 'pointer' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                Teacher
                {key === 'teacher' && (
                  <div style={{ ...sortStyle, width: '14px' }}>
                    <CaretIcon />
                  </div>
                )}
              </span>
            </td>
            <td onClick={() => changeSort('price')} style={{ cursor: 'pointer' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                Price
                {key === 'price' && (
                  <div style={{ ...sortStyle, width: '14px' }}>
                    <CaretIcon />
                  </div>
                )}
              </span>
            </td>
            {currentUser.admin && (
              <td onClick={() => changeSort('improper_package')} style={{ cursor: 'pointer' }}>
                <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly' }}>
                  Improper Package
                  {key === 'improper_package' && (
                    <div style={{ ...sortStyle, width: '14px' }}>
                      <CaretIcon />
                    </div>
                  )}
                </span>
              </td>
            )}
            <td>Order Type</td>
            <td>Customized</td>
            <td>Cover Printed</td>
            <td>Ship Date</td>
            <td>Notes</td>
            <td>Admin Notes</td>
            <td>Edit</td>
            {currentUser.admin && <td>Clear</td>}
            {currentUser.admin && <td>Delete</td>}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((orderPackage, i) => (
            <OrderPackageCells
              key={orderPackage.id}
              school={school}
              orderPackage={orderPackage}
              isAdmin={currentUser.admin}
              removeOrderPackage={removeOrderPackage}
              prevOrderPackage={sortedData[i - 1]}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

Yearbooks.propTypes = {
  currentUser: PropTypes.shape({
    admin: PropTypes.bool,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  onlineOrders: PropTypes.array.isRequired,
}
